// Extracted fields with confidence flags and downloads (requirements 5.5, 5.6, 5.8). A long document that BDA split
// into segments shows one tab per segment, each with its own fields and tables (design 3.5, 4.3 "Long documents").
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Slider from '@cloudscape-design/components/slider';
import FormField from '@cloudscape-design/components/form-field';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';
import Tabs from '@cloudscape-design/components/tabs';
import {
  disallowedValues,
  resultSegments,
  segmentLabel,
  toCsvFiles,
  type FieldValue,
  type NormalizedField,
  type NormalizedResult,
  type NormalizedSegment,
  type NormalizedTable,
  type PreviewRow,
  type PreviewView,
} from '@bda/shared';
import { csvDownload, saveBlob, saveText } from '../../lib/download';
import { useConfidenceThreshold } from './confidenceThreshold';

export { DEFAULT_THRESHOLD, loadThreshold, THRESHOLD_KEY } from './confidenceThreshold';

/**
 * The "Flag fields below this confidence" slider. The threshold is a fraction (0-1), but the slider runs on whole
 * percent (0-100, step 1): with step 0.01, Cloudscape's float check `(value - min) % step !== 0` snaps every value to an
 * accumulated step array, so 100 % showed as 99 % (value 0.99000…03) while the summary used 1.0 (task 17.4).
 */
export function ConfidenceSlider({ threshold, onChange }: { threshold: number; onChange: (v: number) => void }) {
  return (
    <FormField label="Flag fields below this confidence" description={`${Math.round(threshold * 100)}%`}>
      <Slider
        value={Math.round(threshold * 100)}
        min={0}
        max={100}
        step={1}
        onChange={(e) => onChange(Math.round(e.detail.value) / 100)}
        ariaLabel="Confidence threshold"
        valueFormatter={(v) => `${Math.round(v)}%`}
      />
    </FormField>
  );
}

export const isLow = (confidence: number | null, threshold: number): boolean =>
  confidence !== null && confidence < threshold;

/** A value as the Result_View shows it; also used for annotation labels (enlarge-preview-annotate Req 6.5). */
export const formatValue = (v: FieldValue): string =>
  v === null
    ? '-'
    : Array.isArray(v)
      ? v.length === 0
        ? '-'
        : v.map((x) => (typeof x === 'boolean' ? (x ? 'Yes' : 'No') : String(x))).join(', ')
      : String(v);

/** Allowed values by result path (`field`, `group.field`, `table[].column`), from `allowedValuesByPath`. */
export type AllowedValues = Record<string, string[]>;

/**
 * A value, with list items one per line, and a "Not an allowed value" badge for a choice value outside its list.
 * BDA returns "" when no allowed value matched (task 13.7 probe), which is shown as such rather than as a blank.
 */
function Value({ value, options }: { value: FieldValue; options?: string[] }) {
  if (options && (value === '' || (Array.isArray(value) && value.length === 0)))
    return <Box color="text-body-secondary">No allowed value found</Box>;
  const bad = disallowedValues(value, options);
  const text =
    Array.isArray(value) && value.length > 1 ? (
      <ul className="value-list">
        {value.map((x, i) => (
          <li key={i}>{formatValue([x] as FieldValue)}</li>
        ))}
      </ul>
    ) : (
      <span>{formatValue(value)}</span>
    );
  if (bad.length === 0) return text;
  return (
    <SpaceBetween direction="horizontal" size="xxs">
      {text}
      <Badge color="red">Not an allowed value</Badge>
    </SpaceBetween>
  );
}

function Confidence({ value, threshold }: { value: number | null; threshold: number }) {
  if (value === null) return <Box color="text-body-secondary">n/a</Box>;
  const pct = `${Math.round(value * 100)}%`;
  return isLow(value, threshold) ? (
    <SpaceBetween direction="horizontal" size="xxs">
      <span>{pct}</span>
      <Badge color="red">Low confidence</Badge>
    </SpaceBetween>
  ) : (
    <span>{pct}</span>
  );
}

/** Fields table and one table per table field; also used by `SetResultView` (blueprint-sets). */
export function FieldsAndTables({
  fields,
  tables,
  threshold,
  label,
  allowed = {},
}: {
  fields: NormalizedField[];
  tables: NormalizedTable[];
  threshold: number;
  label?: string;
  allowed?: AllowedValues;
}) {
  const suffix = label ? ` (${label})` : '';
  return (
    <>
      <Table
        header={<Header variant="h2">Fields</Header>}
        ariaLabels={label ? { tableLabel: `Fields${suffix}` } : undefined}
        items={fields}
        trackBy="path"
        empty={<Box>No fields were extracted.</Box>}
        columnDefinitions={[
          { id: 'path', header: 'Field', cell: (f) => <code>{f.path}</code>, isRowHeader: true },
          { id: 'value', header: 'Value', cell: (f) => <Value value={f.value} options={allowed[f.path]} /> },
          {
            id: 'confidence',
            header: 'Confidence',
            cell: (f) => <Confidence value={f.confidence} threshold={threshold} />,
          },
        ]}
      />
      {tables.map((t) => (
        <Table
          key={t.name}
          header={
            <Header variant="h2" counter={`(${t.rows.length})`}>
              {t.name}
            </Header>
          }
          ariaLabels={label ? { tableLabel: `${t.name}${suffix}` } : undefined}
          items={t.rows}
          empty={<Box>No rows.</Box>}
          columnDefinitions={t.columns.map((c) => ({
            id: c,
            header: c,
            cell: (r: (typeof t.rows)[number]) => (
              <SpaceBetween direction="horizontal" size="xxs">
                <Value value={r[c]?.value ?? null} options={allowed[`${t.name}[].${c}`]} />
                {isLow(r[c]?.confidence ?? null, threshold) && <Badge color="red">Low confidence</Badge>}
              </SpaceBetween>
            ),
          }))}
        />
      ))}
    </>
  );
}

export const NOT_FOUND_TEXT = 'Not found';
export const PREVIEW_NO_MATCH_TEXT =
  'BDA did not match the sample document to the previewed fields, so no values were extracted.';

const NotFound = () => <Box color="text-body-secondary">{NOT_FOUND_TEXT}</Box>;

/** A field or group row of a preview, flattened so a group is a sub-header row followed by its child rows. */
interface FlatPreviewRow {
  row: Extract<PreviewRow, { kind: 'field' | 'group' }>;
  child: boolean;
}

function flattenPreviewRows(rows: PreviewRow[]): FlatPreviewRow[] {
  const out: FlatPreviewRow[] = [];
  for (const row of rows) {
    if (row.kind === 'table') continue;
    out.push({ row, child: false });
    if (row.kind === 'group') {
      for (const c of row.children) if (c.kind === 'field') out.push({ row: c, child: true });
    }
  }
  return out;
}

/**
 * Preview layout (Req 3.1, 3.3): every field, group, and table of the Preview_Schema in schema order. Found rows render
 * as in a run result; not-found rows show "Not found" with an empty confidence cell.
 */
function PreviewFieldsAndTables({
  rows,
  threshold,
  label,
  allowed = {},
}: {
  rows: PreviewRow[];
  threshold: number;
  label?: string;
  allowed?: AllowedValues;
}) {
  const suffix = label ? ` (${label})` : '';
  const flat = flattenPreviewRows(rows);
  const tables = rows.filter((r): r is Extract<PreviewRow, { kind: 'table' }> => r.kind === 'table');
  return (
    <>
      {flat.length > 0 && (
        <Table
          header={<Header variant="h2">Fields</Header>}
          ariaLabels={label ? { tableLabel: `Fields${suffix}` } : undefined}
          items={flat}
          trackBy={(f) => f.row.path}
          columnDefinitions={[
            {
              id: 'path',
              header: 'Field',
              isRowHeader: true,
              cell: ({ row, child }) =>
                row.kind === 'group' ? (
                  <Box fontWeight="bold">
                    <code>{row.path}</code>
                  </Box>
                ) : (
                  <Box padding={{ left: child ? 'l' : 'n' }}>
                    <code>{row.path}</code>
                  </Box>
                ),
            },
            {
              id: 'value',
              header: 'Value',
              cell: ({ row }) =>
                row.kind === 'group' ? (
                  row.found ? null : (
                    <NotFound />
                  )
                ) : row.found ? (
                  <Value value={row.value} options={allowed[row.path]} />
                ) : (
                  <NotFound />
                ),
            },
            {
              id: 'confidence',
              header: 'Confidence',
              cell: ({ row }) =>
                row.kind === 'field' && row.found ? <Confidence value={row.confidence} threshold={threshold} /> : null,
            },
          ]}
        />
      )}
      {tables.map((t) =>
        t.found ? (
          <Table
            key={t.path}
            header={
              <Header variant="h2" counter={`(${t.table.rows.length})`}>
                {t.path}
              </Header>
            }
            ariaLabels={label ? { tableLabel: `${t.path}${suffix}` } : undefined}
            items={t.table.rows}
            columnDefinitions={t.table.columns.map((c) => ({
              id: c,
              header: c,
              cell: (r: (typeof t.table.rows)[number]) => (
                <SpaceBetween direction="horizontal" size="xxs">
                  <Value value={r[c]?.value ?? null} options={allowed[`${t.path}[].${c}`]} />
                  {isLow(r[c]?.confidence ?? null, threshold) && <Badge color="red">Low confidence</Badge>}
                </SpaceBetween>
              ),
            }))}
          />
        ) : (
          <Container key={t.path} header={<Header variant="h2">{t.path}</Header>}>
            <NotFound />
          </Container>
        ),
      )}
    </>
  );
}

/** One segment's fields and tables; also renders a Set_Run's matched Segment tabs in `SetResultView` (blueprint-sets). */
export function SegmentPanel({
  segment,
  threshold,
  onShowPage,
  allowed,
  previewRows,
}: {
  segment: NormalizedSegment;
  threshold: number;
  onShowPage?: (page: number) => void;
  allowed?: AllowedValues;
  /** Set for an Extraction_Preview: the segment's rows in schema order. */
  previewRows?: PreviewRow[];
}) {
  const label = segmentLabel(segment);
  return (
    <SpaceBetween size="l">
      {onShowPage && (
        <Button
          iconName="file-open"
          onClick={() => onShowPage(segment.pageStart)}
          ariaLabel={`Show page ${segment.pageStart} in the document`}
        >
          Show page {segment.pageStart}
        </Button>
      )}
      {segment.status !== 'MATCH' && (
        <Alert type="warning">
          BDA did not match the blueprint to {label.toLowerCase()} ({segment.status}), so no fields were extracted from
          them.
        </Alert>
      )}
      <FieldsAndTablesOrPreview
        fields={segment.fields}
        tables={segment.tables}
        threshold={threshold}
        label={label}
        allowed={allowed}
        previewRows={previewRows}
      />
    </SpaceBetween>
  );
}

function FieldsAndTablesOrPreview({
  previewRows,
  ...props
}: {
  fields: NormalizedField[];
  tables: NormalizedTable[];
  threshold: number;
  label?: string;
  allowed?: AllowedValues;
  previewRows?: PreviewRow[];
}) {
  return previewRows ? (
    <PreviewFieldsAndTables
      rows={previewRows}
      threshold={props.threshold}
      label={props.label}
      allowed={props.allowed}
    />
  ) : (
    <FieldsAndTables {...props} />
  );
}

export function ResultView({
  result,
  baseName,
  onShowPage,
  allowed,
  preview,
}: {
  result: NormalizedResult;
  baseName: string;
  onShowPage?: (page: number) => void;
  /** Allowed values of the blueprint's choice fields, to flag values outside them. */
  allowed?: AllowedValues;
  /**
   * Extraction_Preview layout (`previewView(schema, result)`): every schema node in schema position, with "Not found"
   * rows (Req 3.1, 3.3, 3.8). The downloads still come from `result`, so they hold the displayed values (Req 3.7).
   */
  preview?: PreviewView;
}) {
  const [threshold, update] = useConfidenceThreshold();
  const segments = resultSegments(result);
  const split = segments.length > 1;
  const rowsOf = (i: number): PreviewRow[] | undefined => (preview ? (preview.segments[i]?.rows ?? []) : undefined);
  const downloadCsv = () => {
    const { blob, fileName } = csvDownload(toCsvFiles(result), baseName);
    saveBlob(blob, fileName);
  };
  return (
    <SpaceBetween size="l">
      <SpaceBetween direction="horizontal" size="xs">
        <Button
          iconName="download"
          onClick={() => saveText(JSON.stringify(result, null, 2), `${baseName}.json`, 'application/json')}
        >
          Download JSON
        </Button>
        <Button iconName="download" onClick={downloadCsv}>
          Download CSV
        </Button>
      </SpaceBetween>
      <ConfidenceSlider threshold={threshold} onChange={update} />
      {preview?.noMatch && <Alert type="warning">{PREVIEW_NO_MATCH_TEXT}</Alert>}
      {split ? (
        <>
          <Alert
            type="info"
            header={`Split into ${segments.length} segments (${segments.map((s) => segmentLabel(s).replace(/^Pages? /, '')).join(', ')})`}
          >
            BDA processes long documents in segments of up to 20 pages and extracts the blueprint fields from each
            segment separately. Each tab shows one segment&apos;s fields, confidence, and tables. The downloads include
            every segment.
          </Alert>
          <Tabs
            ariaLabel="Result segments"
            tabs={segments.map((s, i) => ({
              id: `segment-${s.index}`,
              label: segmentLabel(s),
              content: (
                <SegmentPanel
                  segment={s}
                  threshold={threshold}
                  onShowPage={onShowPage}
                  allowed={allowed}
                  previewRows={rowsOf(i)}
                />
              ),
            }))}
          />
        </>
      ) : (
        <FieldsAndTablesOrPreview
          fields={result.fields}
          tables={result.tables}
          threshold={threshold}
          allowed={allowed}
          previewRows={rowsOf(0)}
        />
      )}
    </SpaceBetween>
  );
}
